'use client';

import { DynamicIcon } from 'phosphor-icons/react';
import { c } from '../../../utils/tailwind-merge';
import { Popover } from '../Popover/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../Tooltip/TooltipRadix';
import type { BadgeProps, BadgePropsContent } from './Badge.types';

const BadgeContent = ({
  text,
  iconName,
  iconPosition = 'before',
}: BadgePropsContent) => (
  <div className="flex items-center gap-1">
    {iconName && iconPosition === 'before' && (
      <DynamicIcon iconName={iconName} size={16} className="shrink-0" />
    )}
    {text}
    {iconName && iconPosition === 'after' && (
      <DynamicIcon iconName={iconName} size={16} className="shrink-0" />
    )}
  </div>
);

type InteractivePopoverProps = Pick<
  BadgePropsContent,
  'text' | 'iconName' | 'iconPosition'
> & {
  content: React.ReactNode;
};

const InteractivePopover = ({
  text,
  iconName,
  iconPosition,
  content,
}: InteractivePopoverProps) => {
  return (
    <Popover>
      <Popover.Trigger
        aria-label={text}
        className="relative flex cursor-pointer items-center border-none bg-transparent p-0 before:absolute before:inset-x-0 before:top-1/2 before:h-6 before:-translate-y-1/2"
      >
        <BadgeContent
          text={text}
          iconName={iconName}
          iconPosition={iconPosition}
        />
      </Popover.Trigger>
      <Popover.Content className={c('z-50')}>{content}</Popover.Content>
    </Popover>
  );
};

export const Badge = ({
  variant = 'default',
  text,
  iconName,
  iconPosition,
  tooltip,
  popover,
  interactive = false,
  className = '',
  tooltipClassName = '',
  'data-test': dataTest,
}: BadgeProps) => {
  return (
    <div
      data-test={dataTest}
      className={c(
        'inline-block whitespace-nowrap rounded-sm px-1 py-0.5 text-xs font-normal tracking-tighter',
        variant === 'default' && 'bg-surface-muted text-content-muted',
        variant === 'sale' && 'bg-danger-surface text-danger',
        className
      )}
    >
      {!tooltip && !popover ? (
        <BadgeContent
          text={text}
          iconName={iconName}
          iconPosition={iconPosition}
        />
      ) : interactive || popover ? (
        <InteractivePopover
          text={text}
          iconName={iconName}
          iconPosition={iconPosition}
          content={popover || tooltip}
        />
      ) : (
        <Tooltip>
          <TooltipTrigger
            aria-label={text}
            className="relative flex items-center border-none bg-transparent p-0 before:absolute before:inset-x-0 before:top-1/2 before:h-6 before:-translate-y-1/2"
          >
            <BadgeContent
              text={text}
              iconName={iconName}
              iconPosition={iconPosition}
            />
          </TooltipTrigger>
          <TooltipContent className={tooltipClassName}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
